<script setup>
import { computed, ref } from "vue";

/**
 * Значок юриста.
 *
 * Цвет включается только там, где значок объясняется, — в профиле. В списке
 * карточек он серый: четыре разных цвета на десяти карточках превратились бы в
 * конфетти, а у нас и так индиго-интерфейс и красный для звонка.
 */
const props = defineProps({
  perk: { type: Object, required: true },
  size: { type: Number, default: 16 },
  mono: { type: Boolean, default: false },
  // Приглушённый режим для карточки каталога: рисунок обесцвечен и оживает
  // цветом при наведении. Рядом с фотографией юриста четыре цветных человечка
  // читались как ещё четыре человека — глаз честно пытался узнать в них лица.
  muted: { type: Boolean, default: false },
});

const imgFailed = ref(false);

/**
 * Картинку показываем только там, где она читается. Проверка размером показала:
 * лицо живёт примерно с 32 пикселей, ниже остаётся пятно — поэтому на карточке
 * каталога (15 px) остаётся вектор, а рисунок включается в профиле.
 *
 * Если файла ещё нет, img даст ошибку и мы молча вернёмся к вектору: значки
 * появляются по мере готовности рисунков, а не все разом.
 */
const useImage = computed(() => !!props.perk.image && !props.mono && props.size >= 24 && !imgFailed.value);

const FILL = {
  amber: "#b4762a",
  indigo: "#3d5aa8",
  emerald: "#2c7d5a",
  violet: "#6f4a9e",
  sky: "#2f7ea8",
  rose: "#b5485d",
};

const fill = computed(() => (props.mono ? "#6b7280" : FILL[props.perk.color] || "#6b7280"));

/** Ранги стажа рисуются шевронами: чем выше ранг, тем их больше. */
const chevrons = computed(() => {
  const k = props.perk.key;
  if (k === "master") return 3;
  if (k === "hardened") return 3;
  if (k === "veteran") return 2;
  return 1;
});
</script>

<template>
  <img
    v-if="useImage"
    :src="perk.image"
    :alt="'Значок «' + perk.name + '»'"
    :width="size"
    :height="size"
    loading="lazy"
    decoding="async"
    class="shrink-0 select-none transition duration-200"
    :class="muted ? 'grayscale contrast-[.9] opacity-[.85] group-hover/perk:grayscale-0 group-hover/perk:opacity-100' : ''"
    @error="imgFailed = true"
  />

  <svg v-else :width="size" :height="size" viewBox="0 0 112 112" role="img"
    :aria-label="'Значок «' + perk.name + '»'" class="shrink-0">
    <!-- Круг, а не скруглённый квадрат: рядом стоят нарисованные значки, и они
         круглые. Квадратная заглушка среди них читалась как чужая. -->
    <rect width="112" height="112" rx="56" :fill="fill" />

    <!-- Стаж: шевроны -->
    <template v-if="perk.auto">
      <path v-if="chevrons >= 1" d="M56 34 80 58 68 58 56 46 44 58 32 58Z" fill="#fff" />
      <path v-if="chevrons >= 2" d="M56 58 80 82 68 82 56 70 44 82 32 82Z" fill="#fff" opacity=".72" />
      <path v-if="chevrons >= 3" d="M56 76 80 100 68 100 56 88 44 100 32 100Z" fill="#fff" opacity=".45" />
    </template>

    <!-- Оратор: микрофон — тем же предметом значок и проверяется -->
    <template v-else-if="perk.key === 'orator'">
      <rect x="47" y="26" width="18" height="34" rx="9" fill="#fff" />
      <path d="M38 54a18 18 0 0 0 36 0" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round" />
      <path d="M56 72v12M44 88h24" stroke="#fff" stroke-width="6" stroke-linecap="round" />
    </template>

    <!-- Харизма: реплика с галочкой -->
    <template v-else-if="perk.key === 'charisma'">
      <path d="M28 30h44a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6H48l-15 13V66h-5a6 6 0 0 1-6-6V36a6 6 0 0 1 6-6Z" fill="#fff" />
      <path d="M40 48l8 8 15-15" fill="none" :stroke="fill" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" />
    </template>

    <!-- Наставник: академическая шапочка — читается даже в 24 пикселя -->
    <template v-else-if="perk.key === 'mentor'">
      <path d="M56 26 96 44 56 62 16 44Z" fill="#fff" />
      <path d="M34 52v20c0 7 10 13 22 13s22-6 22-13V52" fill="none" stroke="#fff" stroke-width="7" stroke-linecap="round" />
      <path d="M90 48v22" stroke="#fff" stroke-width="6" stroke-linecap="round" />
    </template>

    <!-- Восприятие: глаз. Он же достаётся значку, которого мы ещё не нарисовали. -->
    <template v-else>
      <path d="M22 56c11-17 23-25 34-25s23 8 34 25c-11 17-23 25-34 25s-23-8-34-25Z" fill="#fff" />
      <circle cx="56" cy="56" r="14" :fill="fill" />
      <circle cx="56" cy="56" r="5" fill="#fff" />
    </template>
  </svg>
</template>
